/**
 * ExportEngine — render offline, normalização, limiter e codificação.
 *
 * Usa OfflineAudioContext com a MESMA cadeia de efeitos da reprodução
 * (buildFxChain), portanto o arquivo exportado é o que o usuário ouviu.
 *
 * Formatos:
 *  - WAV 16/24 bit (sempre disponível, codificador próprio);
 *  - OGG/Opus ou WEBM/Opus via MediaRecorder (quando o navegador suporta);
 *  - MP3 via lamejs (carregado sob demanda; se não carregar, a opção some).
 */
import type { ExportSettings, FxConfig, MixSettings, Track } from '../types.js';
import { buildFxChain, configureFx, type CtxLike } from './fxChain.js';
import { createImpulseResponse, REVERB_SPECS } from './fx.js';
import { audioBufferToWav, encodeWav } from './wav.js';
import { applyGainSoft, peak } from './analysis.js';
import { applyNoiseGate } from './offlineFx.js';
import { reduceSibilance } from './noiseReduction.js';
import { clamp, gainToDb, dbToGain } from '../utils/misc.js';

export interface RenderTrackInput {
  id: string;
  name: string;
  data: Float32Array; // mono
  sampleRate: number;
  offsetSec: number; // posição na linha do tempo
  volume: number;
  pan: number;
  fx: FxConfig;
  muted?: boolean;
  useDenoised?: boolean;
  denoised?: Float32Array | null;
}

export interface RenderRequest {
  sampleRate: number;
  duration: number;
  instrumental?: { left: Float32Array; right: Float32Array; sampleRate: number; volume: number; pan: number; fadeInMs: number; fadeOutMs: number; muted?: boolean } | null;
  tracks: RenderTrackInput[];
  masterVolume: number;
  limiter: boolean;
  limitDb: number;
  metronome?: null;
}

export interface RenderResult {
  buffer: AudioBuffer;
  peak: number;
  peakDb: number;
  clipping: boolean;
  duration: number;
  sampleRate: number;
}

export interface EncodeResult {
  blob: Blob;
  mime: string;
  extension: string;
  bitrateKbps?: number;
  sampleRate: number;
  bitDepth?: number;
  encoder: string;
  fallback?: string;
}

export interface ExportCapability {
  wav: boolean;
  mp3: boolean;
  ogg: boolean;
  mp3Reason?: string;
  oggReason?: string;
}

/** Cria um AudioContext offline compatível (fallback para testes). */
function createOffline(sampleRate: number, duration: number, channels = 2): OfflineAudioContext {
  const len = Math.max(1, Math.ceil(duration * sampleRate));
  const Ctor: typeof OfflineAudioContext =
    (globalThis as any).OfflineAudioContext || (globalThis as any).webkitOfflineAudioContext;
  return new Ctor(channels, len, sampleRate);
}

export class ExportEngine {
  private lamePromise: Promise<any> | null = null;

  /** Renderiza a mix (ou um stem) num OfflineAudioContext. */
  async render(req: RenderRequest): Promise<RenderResult> {
    const ctx = createOffline(req.sampleRate, Math.max(0.2, req.duration), 2);
    const worklets = await this.loadOfflineWorklets(ctx);

    // ---- master ----
    const masterIn = ctx.createGain();
    const limiterEnabled = ctx.createGain();
    const limiterBypass = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = clamp(req.limitDb, -30, 0);
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.12;
    const masterVol = ctx.createGain();
    masterVol.gain.value = clamp(req.masterVolume, 0, 1.5);
    masterIn.connect(limiterEnabled);
    masterIn.connect(limiterBypass);
    limiterEnabled.gain.value = req.limiter ? 1 : 0;
    limiterBypass.gain.value = req.limiter ? 0 : 1;
    limiterEnabled.connect(limiter);
    limiter.connect(masterVol);
    limiterBypass.connect(masterVol);
    masterVol.connect(ctx.destination);

    // ---- instrumental ----
    if (req.instrumental && !req.instrumental.muted) {
      const inst = req.instrumental;
      const faded = scaleForFades(inst.left, inst.right, inst.sampleRate, inst.fadeInMs, inst.fadeOutMs);
      const len = Math.max(1, Math.max(faded.left.length, faded.right.length));
      const buf = ctx.createBuffer(2, len, inst.sampleRate);
      buf.copyToChannel(stableFloat32(faded.left), 0);
      buf.copyToChannel(stableFloat32(faded.right), 1);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const g = ctx.createGain();
      g.gain.value = clamp(inst.volume, 0, 2);
      const p = ctx.createStereoPanner();
      p.pan.value = clamp(inst.pan, -1, 1);
      src.connect(g);
      g.connect(p);
      p.connect(masterIn);
      src.start(0);
    }

    // ---- faixas vocais ----
    for (const t of req.tracks) {
      if (t.muted) continue;
      let data = (t.useDenoised && t.denoised?.length ? t.denoised : t.data) as Float32Array;
      if (!data || !data.length) continue;
      let fx = t.fx;
      // Fallbacks honestos se o navegador não oferecer AudioWorklet offline.
      if (fx.gate.enabled && !worklets['vocal-gate']) {
        data = applyNoiseGate(data, t.sampleRate, {
          thresholdDb: fx.gate.thresholdDb,
          attackMs: fx.gate.attackMs,
          releaseMs: fx.gate.releaseMs,
          holdMs: fx.gate.holdMs,
        });
        fx = { ...fx, gate: { ...fx.gate, enabled: false } };
      }
      if (fx.deEsser.enabled && !worklets['vocal-deesser']) {
        data = reduceSibilance(data, t.sampleRate, fx.deEsser.strength);
        fx = { ...fx, deEsser: { ...fx.deEsser, enabled: false } };
      }
      // offsetSec positivo atrasa o arquivo; negativo adianta (pula amostras
      // iniciais). O sincronizador passa o sinal inverso do nudge do player.
      const delaySamples = Math.max(0, Math.round(t.offsetSec * t.sampleRate));
      const skipSamples = Math.max(0, Math.round(-t.offsetSec * t.sampleRate));
      const playable = data.subarray(Math.min(skipSamples, data.length));
      const total = delaySamples + playable.length;
      const buf = ctx.createBuffer(1, Math.max(1, total), t.sampleRate);
      buf.copyToChannel(stableFloat32(placeAt(playable, delaySamples, Math.max(1,total))), 0);
      const src = ctx.createBufferSource();
      src.buffer = buf;

      // Buses independentes: cada faixa mantém seu próprio preset de reverb e delay.
      const reverbIn = ctx.createGain();
      const convolver = ctx.createConvolver();
      convolver.buffer = createImpulseResponse(ctx as unknown as { sampleRate: number; createBuffer: any }, REVERB_SPECS[fx.reverb.preset]);
      const reverbWet = ctx.createGain();
      reverbIn.connect(convolver);
      convolver.connect(reverbWet);
      reverbWet.connect(masterVol);

      const delayIn = ctx.createGain();
      const delay = ctx.createDelay(2);
      const feedback = ctx.createGain();
      const tone = ctx.createBiquadFilter();
      tone.type = 'lowpass'; tone.frequency.value = 4200;
      const delayWet = ctx.createGain();
      delay.delayTime.value = clamp(fx.delay.timeMs / 1000, 0.01, 1.5);
      feedback.gain.value = clamp(fx.delay.feedback, 0, 0.9);
      delayIn.connect(delay); delay.connect(tone); tone.connect(feedback);
      feedback.connect(delay); delay.connect(delayWet); delayWet.connect(masterVol);

      const { refs, input, output } = buildFxChain({
        ctx: ctx as unknown as CtxLike,
        sends: { reverbIn, delayIn },
        worklets,
      });
      configureFx(refs, fx, 0);

      const vol = ctx.createGain();
      vol.gain.value = clamp(t.volume, 0, 2);
      const pan = ctx.createStereoPanner();
      pan.pan.value = clamp(t.pan, -1, 1);
      src.connect(input);
      // input percorre a cadeia FX; conectá-lo diretamente a output seria bypass.
      output.connect(vol);
      vol.connect(pan);
      pan.connect(masterIn);
      src.start(0, 0);
      // Fallback de double vocal quando OfflineAudioWorklet não está disponível:
      // cópia com micro-atraso + modulação de atraso para evitar comb estático.
      if (fx.double.enabled && !worklets['vocal-detune']) {
        const doubled = makeModulatedDouble(playable, t.sampleRate, fx.double.delayMs, fx.double.detuneCents);
        const doubleTotal = delaySamples + doubled.length;
        const doubleBuffer = ctx.createBuffer(1, Math.max(1,doubleTotal), t.sampleRate);
        doubleBuffer.copyToChannel(stableFloat32(placeAt(doubled,delaySamples,Math.max(1,doubleTotal))),0);
        const doubleSource = ctx.createBufferSource();
        doubleSource.buffer = doubleBuffer;
        const doubleGain = ctx.createGain();
        doubleGain.gain.value = dbToGain(fx.double.levelDb);
        const doublePan = ctx.createStereoPanner();
        doublePan.pan.value = clamp(fx.double.pan,-1,1);
        doubleSource.connect(doubleGain);
        doubleGain.connect(doublePan);
        doublePan.connect(input);
        doubleSource.start(0,0);
      }
    }

    const rendered = await ctx.startRendering();
    let p = 0;
    for (let c = 0; c < rendered.numberOfChannels; c++) {
      const d = rendered.getChannelData(c);
      for (let i = 0; i < d.length; i++) {
        const a = Math.abs(d[i]);
        if (a > p) p = a;
      }
    }
    return {
      buffer: rendered,
      peak: p,
      peakDb: gainToDb(p),
      clipping: p >= 0.999,
      duration: rendered.duration,
      sampleRate: rendered.sampleRate,
    };
  }

  private async loadOfflineWorklets(ctx: OfflineAudioContext): Promise<Record<string, boolean>> {
    const worklet = (ctx as any).audioWorklet as AudioWorklet | undefined;
    if (!worklet?.addModule) return {};
    const files: Record<string,string> = {
      'vocal-gate': new URL('../workers/gate-processor.js', import.meta.url).href,
      'vocal-deesser': new URL('../workers/deesser-processor.js', import.meta.url).href,
      'vocal-detune': new URL('../workers/detune-processor.js', import.meta.url).href,
    };
    const available: Record<string,boolean> = {};
    for (const [name,url] of Object.entries(files)) {
      try { await worklet.addModule(url); available[name] = true; }
      catch (err) { console.info(`[ExportEngine] ${name} indisponível em OfflineAudioContext; usando fallback.`,err); available[name] = false; }
    }
    return available;
  }

  /** Normaliza (opcional) + soft clip de proteção, sem destruir dinâmica. */
  normalizeBuffer(buffer: AudioBuffer, settings: ExportSettings): { buffer: AudioBuffer; appliedDb: number; clipped: boolean } {
    if (!settings.normalize) return { buffer, appliedDb: 0, clipped: peakOfBuffer(buffer) >= 0.999 };
    const p = peakOfBuffer(buffer);
    if (p <= 0) return { buffer, appliedDb: 0, clipped: false };
    const target = Math.pow(10, settings.normalizePeakDb / 20);
    const gain = target / p;
    const appliedDb = 20 * Math.log10(gain);
    const out = cloneAudioBuffer(buffer);
    for (let c = 0; c < buffer.numberOfChannels; c++) {
      const d = buffer.getChannelData(c);
      const nd = applyGainSoft(d, gain, 0.9995);
      out.getChannelData(c).set(nd);
    }
    return { buffer: out, appliedDb, clipped: peakOfBuffer(out) >= 0.999 };
  }

  /** WAV 16/24 bit com dither opcional. */
  encodeWavFromBuffer(buffer: AudioBuffer, bitDepth: 16 | 24, dither: boolean): EncodeResult {
    const ab = audioBufferToWav(buffer, bitDepth, { dither });
    return {
      blob: new Blob([ab], { type: 'audio/wav' }),
      mime: 'audio/wav',
      extension: 'wav',
      sampleRate: buffer.sampleRate,
      bitDepth,
      encoder: 'WAV PCM (encoder próprio)',
    };
  }

  /** MP3 via lamejs (carregado sob demanda). */
  async encodeMp3(buffer: AudioBuffer, quality: ExportSettings['quality']): Promise<EncodeResult | null> {
    const lame = await this.loadLame();
    if (!lame) return null;
    const bitrate = quality === 'maxima' ? 320 : quality === 'alta' ? 256 : 192;
    const channels = Math.min(2, buffer.numberOfChannels) as 1 | 2;
    const mp3 = new lame.Mp3Encoder(channels, buffer.sampleRate, bitrate);
    const left = toInt16(buffer.getChannelData(0));
    const right = channels === 2 ? toInt16(buffer.getChannelData(1)) : left;
    const blockSize = 1152;
    const parts: Int8Array[] = [];
    for (let i = 0; i < left.length; i += blockSize) {
      const l = left.subarray(i, i + blockSize);
      const r = right.subarray(i, i + blockSize);
      const chunk = channels === 2 ? mp3.encodeBuffer(l, r) : mp3.encodeBuffer(l);
      if (chunk.length) parts.push(chunk);
    }
    const end = mp3.flush();
    if (end.length) parts.push(end);
    const blob = new Blob(parts as unknown as BlobPart[], { type: 'audio/mpeg' });
    if (blob.size < 1024) return null;
    return {
      blob,
      mime: 'audio/mpeg',
      extension: 'mp3',
      bitrateKbps: bitrate,
      sampleRate: buffer.sampleRate,
      encoder: 'LAME (lamejs)',
    };
  }

  /** OGG/Opus (ou WEBM/Opus quando OGG não é suportado) via MediaRecorder. */
  async encodeOgg(buffer: AudioBuffer, preferOgg = true): Promise<EncodeResult | null> {
    if (typeof MediaRecorder === 'undefined') return null;
    const AC: typeof AudioContext = (globalThis as any).AudioContext || (globalThis as any).webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC({ sampleRate: buffer.sampleRate });
    const dest = ctx.createMediaStreamDestination();
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(dest);
    const mime = preferOgg && MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
      ? 'audio/ogg;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : '';
    if (!mime) {
      ctx.close().catch(() => {});
      return null;
    }
    const rec = new MediaRecorder(dest.stream, { mimeType: mime, audioBitsPerSecond: 192000 });
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const done = new Promise<Blob>((resolve) => {
      rec.onstop = () => resolve(new Blob(chunks, { type: mime }));
    });
    rec.start(100);
    src.start(0);
    await new Promise((r) => setTimeout(r, buffer.duration * 1000 + 400));
    try {
      rec.stop();
    } catch {
      /* noop */
    }
    const blob = await done;
    src.disconnect();
    ctx.close().catch(() => {});
    if (blob.size < 1024) return null;
    return {
      blob,
      mime,
      extension: mime.includes('ogg') ? 'ogg' : 'webm',
      sampleRate: buffer.sampleRate,
      encoder: 'MediaRecorder (Opus)',
      fallback: mime.includes('ogg') ? undefined : 'OGG não suportado neste navegador; geramos WEBM/Opus.',
    };
  }

  async loadLame(): Promise<any | null> {
    if (this.lamePromise) return this.lamePromise;
    this.lamePromise = (async () => {
      try {
        const mod: any = await import('lamejs');
        const lame = mod?.default ?? mod;
        if (lame?.Mp3Encoder) return lame;
        // alguns builds expõem como namespace
        if (mod?.Mp3Encoder) return mod;
        return null;
      } catch (err) {
        console.warn('[ExportEngine] lamejs indisponível:', err);
        return null;
      }
    })();
    return this.lamePromise;
  }

  /** O que este navegador realmente consegue exportar (sem prometer o que não faz). */
  async capabilities(): Promise<ExportCapability> {
    const lame = await this.loadLame();
    const ogg =
      typeof MediaRecorder !== 'undefined' &&
      (MediaRecorder.isTypeSupported?.('audio/ogg;codecs=opus') ||
        MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus'));
    return {
      wav: true,
      mp3: !!lame,
      mp3Reason: lame ? undefined : 'Encoder MP3 não pôde ser carregado neste dispositivo.',
      ogg: !!ogg,
      oggReason: ogg ? undefined : 'Este navegador não gera OGG/Opus no cliente.',
    };
  }

  /** Codifica conforme as configurações, com fallback explícito para WAV. */
  async encode(buffer: AudioBuffer, settings: ExportSettings): Promise<EncodeResult> {
    const bitDepth: 16 | 24 = settings.quality === 'normal' ? 16 : settings.bitDepth;
    if (settings.format === 'mp3') {
      const mp3 = await this.encodeMp3(buffer, settings.quality);
      if (mp3) return mp3;
      return {
        ...this.encodeWavFromBuffer(buffer, bitDepth, settings.dither),
        fallback: 'MP3 indisponível neste dispositivo — exportamos WAV sem perdas.',
      };
    }
    if (settings.format === 'ogg') {
      const ogg = await this.encodeOgg(buffer);
      if (ogg) return ogg;
      return {
        ...this.encodeWavFromBuffer(buffer, bitDepth, settings.dither),
        fallback: 'OGG/Opus indisponível neste navegador — exportamos WAV sem perdas.',
      };
    }
    return this.encodeWavFromBuffer(buffer, bitDepth, settings.dither);
  }

  /** Amostragem/profundidade conforme a qualidade escolhida. */
  static settingsToRate(settings: ExportSettings): number {
    return settings.quality === 'normal' ? Math.min(44100, settings.sampleRate) : settings.sampleRate;
  }

  /** Gera um WAV direto de Float32Array (útil para stems mono). */
  static wavFromMono(data: Float32Array, sampleRate: number, bitDepth: 16 | 24 = 16): Blob {
    return new Blob([encodeWav([data], sampleRate, bitDepth)], { type: 'audio/wav' });
  }
}

// ----------------------------------------------------------------------
// helpers
// ----------------------------------------------------------------------

function peakOfBuffer(buffer: AudioBuffer): number {
  let p = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const d = buffer.getChannelData(c);
    for (let i = 0; i < d.length; i++) {
      const a = Math.abs(d[i]);
      if (a > p) p = a;
    }
  }
  return p;
}

function toInt16(f: Float32Array): Int16Array {
  const out = new Int16Array(f.length);
  for (let i = 0; i < f.length; i++) {
    const s = Math.max(-1, Math.min(1, f[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Aplica fade in/out em L/R (cópia — o arquivo original nunca muda). */
function scaleForFades(
  left: Float32Array,
  right: Float32Array,
  sampleRate: number,
  fadeInMs: number,
  fadeOutMs: number
): { left: Float32Array; right: Float32Array } {
  const n = Math.max(left.length, right.length);
  const l = Float32Array.from(left);
  const r = Float32Array.from(right);
  const fi = Math.min(n, Math.round((fadeInMs / 1000) * sampleRate));
  const fo = Math.min(n, Math.round((fadeOutMs / 1000) * sampleRate));
  for (let i = 0; i < fi; i++) {
    const g = i / Math.max(1, fi);
    l[i] *= g;
    r[i] *= g;
  }
  for (let i = 0; i < fo; i++) {
    const idx = n - 1 - i;
    const g = i / Math.max(1, fo);
    l[idx] *= g;
    r[idx] *= g;
  }
  return { left: l, right: r };
}

/** Coloca `data` a partir de `offset` num array de tamanho `total`. */
function placeAt(data: Float32Array, offset: number, total: number): Float32Array {
  const out = new Float32Array(total);
  const n = Math.min(data.length, total - offset);
  if (n > 0) out.set(data.subarray(0, n), offset);
  return out;
}

/** Faz cópia própria (ArrayBuffer normal), compatível com AudioBuffer.copyToChannel. */
function stableFloat32(data: Float32Array): Float32Array<ArrayBuffer> {
  const out = new Float32Array(data.length) as Float32Array<ArrayBuffer>;
  out.set(data);
  return out;
}

/** Cópia double fallback: microdelay + microvariação de pitch/delay. */
function makeModulatedDouble(data: Float32Array, sampleRate: number, delayMs: number, cents: number): Float32Array {
  const out = new Float32Array(data.length);
  const base = Math.max(1, Math.round((Math.max(5,delayMs)/1000)*sampleRate));
  const depth = Math.max(.25,Math.min(sampleRate*.004,Math.abs(cents)/100*(sampleRate/220)*.6+.35));
  const rate = .35;
  for(let i=0;i<out.length;i++){
    const t=i/sampleRate;
    const delay=base+Math.sin(2*Math.PI*rate*t)*depth;
    const src=i-delay;
    if(src<0)continue;
    const a=Math.floor(src),b=Math.min(data.length-1,a+1),f=src-a;
    out[i]=(data[a]??0)*(1-f)+(data[b]??0)*f;
  }
  return out;
}

/** Clone próprio para que a normalização nunca altere o render fonte. */
function cloneAudioBuffer(source: AudioBuffer): AudioBuffer {
  const init = { numberOfChannels: source.numberOfChannels, length: source.length, sampleRate: source.sampleRate };
  let target: AudioBuffer;
  try {
    const Ctor = (globalThis as any).AudioBuffer;
    target = new Ctor(init) as AudioBuffer;
  } catch {
    const Ctor = (globalThis as any).OfflineAudioContext || (globalThis as any).webkitOfflineAudioContext;
    target = new Ctor(source.numberOfChannels, source.length, source.sampleRate).createBuffer(
      source.numberOfChannels, source.length, source.sampleRate
    ) as AudioBuffer;
  }
  for (let c = 0; c < source.numberOfChannels; c++) target.getChannelData(c).set(source.getChannelData(c));
  return target;
}

export { peakOfBuffer, dbToGain };
